import React from 'react';
import type SignatureCanvasType from 'react-signature-canvas';
import type { FormPayer, IntakeFormField, IntakeFormSection } from '../../../api/bookingApi';
import { isBirthdayField, type BirthdayParts } from '../../custom-form/birthday';
import CustomFormField from '../../custom-form/CustomFormField';
import { FileUploadField, type UploadMeta } from '../FileUploadField';
import { prepareUploadFile } from '../prepareUploadFile';
import { isFieldVisible } from '../fieldVisibility';
import { isFieldRequired } from '../fieldRequiredness';
import { SavedSignatureNote } from '../V2Bits';
import { PayerTypeahead, type PayerConfirmView } from '../PayerTypeahead';
import {
  V2_FILE_UPLOAD_POLICY,
  isFileUploadFieldKey,
  slotKeys,
  type FileUploadFieldKey,
  type SlotKey,
  type StagedUpload,
  type V2Item,
} from '../types';

export interface V2SectionStepProps {
  item: V2Item;
  section: IntakeFormSection;
  fields: IntakeFormField[];
  sectionIndex?: number;
  sectionCount?: number;
  partIndex?: number;
  partCount?: number;
  clientId: string;
  token: string;
  values: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
  birthdayParts?: Record<string, BirthdayParts>;
  onBirthdayPartChange?: (key: string, part: keyof BirthdayParts, value: string) => void;
  fieldErrors: Record<string, string>;
  /** Fields an insurance check flagged, keyed like fieldErrors; '' highlights without text. */
  fieldHighlights: Record<string, string>;
  /** Read-only while an insurance check runs: its outcome is about the answers sent. */
  disabled?: boolean;
  /** Session-wide: payer search failed, so every payer field is plain text. */
  payerFallback: boolean;
  onPayerFallback: (key: string) => void;
  onPayerPick: (key: string, payer: FormPayer) => void;
  /** Payer fields the patient is asked to confirm, by field key (booking-frontend#51). */
  payerConfirms: Record<string, PayerConfirmView>;
  onPayerNotListed: (key: string) => void;
  /** A payer field's latest search answered (Next reuses it for the same text). */
  onPayerResults: (key: string, query: string, payers: FormPayer[]) => void;
  uploadMeta: Record<string, UploadMeta>;
  onUploadMetaChange: (key: string, meta: UploadMeta) => void;
  /** Drops the staged slot AND the preview meta for the field. */
  onUploadMetaRemove: (key: string) => void;
  onAutoFill: (mapping: Record<string, string>) => void;
  /** Stages a picked file for the field; the renderer owns error/link handling. */
  stageFileForField: (fieldKey: FileUploadFieldKey, file: File) => Promise<{ value: unknown; fieldMapping?: Record<string, string> | null }>;
  sigRefs: React.MutableRefObject<Map<string, SignatureCanvasType | null>>;
  stagedUploads: Record<SlotKey, StagedUpload>;
}

export function V2SectionStep(props: V2SectionStepProps) {
  const { item, section, fields, sectionIndex, sectionCount, partIndex, partCount, fieldErrors, fieldHighlights, disabled, values } = props;
  const itemId = item.instanceItemId;
  // Follow-ups whose show_if is unmet are dropped from the screen, so a "yes/no"
  // reveals its dependent field only once answered (instead of dozens of empties).
  const visibleFields = fields.filter((f) => isFieldVisible(f, values, item.sections))
    .map((field) => ({ ...field, required: isFieldRequired(item, field, values) }));
  return (
    <div className="py-2">
      <div className="mb-6">
        <span className="text-xs font-bold text-primary-deep uppercase tracking-widest">
          {item.name}
          {(sectionCount ?? 0) > 1 && (
            <span className="text-gray-500 font-medium normal-case tracking-normal"> · {sectionIndex} of {sectionCount}</span>
          )}
        </span>
        <h3 className="text-[22px] leading-tight sm:text-2xl font-extrabold text-gray-900 mt-1">
          {section.label || item.name}
        </h3>
        {(partCount ?? 0) > 1 && (
          <p className="text-sm font-medium text-gray-500 mt-1">Part {partIndex} of {partCount}</p>
        )}
      </div>
      {/* One natural page scroll: fields flow in the page, never a nested box.
          scroll-mt/-mb keep a scrolled-to field clear of the sticky bars. */}
      <fieldset disabled={disabled} aria-busy={disabled || undefined} className="min-w-0 disabled:pointer-events-none">
        <div className="space-y-5">
          {visibleFields.map((field) => {
            const fieldErr = fieldErrors[`${itemId}:${field.key}`];
            const highlight = fieldHighlights[`${itemId}:${field.key}`];
            const highlighted = highlight !== undefined && !fieldErr;
            const inputId = `pkginput-${itemId}-${field.key}`;
            const birthday = isBirthdayField(field);
            const errorId = birthday && fieldErr ? `pkgerror-${itemId}-${field.key}` : undefined;
            const labelId = `pkglabel-${itemId}-${field.key}`;
            // A single native control gets the label via htmlFor; a field made of
            // several controls (option lists, canvases, file actions) is a group
            // the label names instead.
            const singleControl = ['text', 'email', 'phone', 'number', 'textarea', 'address', 'select', 'date']
              .indexOf(field.field_type) !== -1;
            const showLabel = !birthday && field.field_type !== 'static_text' && field.field_type !== 'checkbox';
            return (
              <div
                key={field.key}
                id={`pkgfield-${itemId}-${field.key}`}
                className={`scroll-mt-24 scroll-mb-28 ${highlighted ? 'rounded-xl ring-2 ring-amber-400 ring-offset-4 ring-offset-white' : ''}`}
                data-insurance-highlight={highlighted ? 'true' : undefined}
              >
                {/* static_text is a paragraph and CustomFormField's checkbox carries its own inline label */}
                {showLabel && (
                  <label id={labelId} htmlFor={singleControl ? inputId : undefined} className="block text-sm font-semibold text-gray-700 mb-1.5">
                    {field.label}
                    {field.required && <span className="text-primary-deep ml-1">*</span>}
                  </label>
                )}
                {showLabel && !singleControl ? (
                  <div role="group" aria-labelledby={labelId}>
                    <FieldControl {...props} field={field} inputId={inputId} birthdayErrorId={errorId} />
                  </div>
                ) : (
                  <FieldControl {...props} field={field} inputId={inputId} birthdayErrorId={errorId} />
                )}
                {fieldErr && <p id={errorId} className="text-red-500 text-xs mt-1 font-medium">{fieldErr}</p>}
                {highlighted && highlight && <p role="alert" className="text-amber-800 text-xs mt-1.5 font-medium">{highlight}</p>}
              </div>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}

// One field's input control on a section screen. static_text is read-only copy;
// files stage through the v2 uploader; everything else renders via CustomFormField
// with signature canvases staging on the section's Next.
function FieldControl(props: V2SectionStepProps & { field: IntakeFormField; inputId: string; birthdayErrorId?: string }) {
  const { item, field, inputId, values, onFieldChange, uploadMeta, onUploadMetaChange, onUploadMetaRemove, onAutoFill, stageFileForField, sigRefs, stagedUploads, clientId, token, payerFallback, onPayerFallback, onPayerPick, payerConfirms, onPayerNotListed, onPayerResults } = props;
  const itemId = item.instanceItemId;
  if (field.field_type === 'static_text') {
    return (
      <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4 text-[15px] sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">
        {field.content ?? ''}
      </div>
    );
  }
  if (field.field_type === 'file_upload' && isFileUploadFieldKey(field.key)) {
    const fieldKey = field.key;
    return (
      <FileUploadField
        field={field}
        value={values[fieldKey]}
        onChange={(v) => onFieldChange(fieldKey, v)}
        uploadMeta={uploadMeta}
        onUploadMetaChange={onUploadMetaChange}
        onUploadMetaRemove={onUploadMetaRemove}
        onAutoFill={onAutoFill}
        policy={V2_FILE_UPLOAD_POLICY}
        uploader={(file) => stageFileForField(fieldKey, file)}
        preprocess={(file) => prepareUploadFile(file, field.accepted_file_types)}
        captureActions
      />
    );
  }
  if (field.field_type === 'text' && field.payer_id_key) {
    const companion = values[field.payer_id_key];
    return (
      <PayerTypeahead
        field={field}
        inputId={inputId}
        value={values[field.key]}
        picked={typeof companion === 'string' && companion.trim() !== ''}
        clientId={clientId}
        token={token}
        fallback={payerFallback}
        onChange={(v) => onFieldChange(field.key, v)}
        onPick={(payer) => onPayerPick(field.key, payer)}
        onFallback={() => onPayerFallback(field.key)}
        confirm={payerConfirms[field.key] ?? null}
        onNotListed={() => onPayerNotListed(field.key)}
        onResults={(query, payers) => onPayerResults(field.key, query, payers)}
      />
    );
  }
  return (
    <>
      <CustomFormField
        field={field}
        value={values[field.key]}
        onChange={(v) => onFieldChange(field.key, v)}
        sigRef={(el) => {
          if (el) sigRefs.current.set(`${itemId}:${field.key}`, el);
        }}
        onClearSig={() => sigRefs.current.get(`${itemId}:${field.key}`)?.clear()}
        birthdayParts={props.birthdayParts?.[`${itemId}:${field.key}`]}
        onBirthdayPartChange={props.onBirthdayPartChange ? (part, value) => props.onBirthdayPartChange!(field.key, part, value) : undefined}
        birthdayErrorId={props.birthdayErrorId}
        inputId={inputId}
      />
      {(field.field_type === 'signature' || field.field_type === 'initials') && (
        <SavedSignatureNote staged={stagedUploads[slotKeys.fieldSig(itemId, field.key)]} />
      )}
    </>
  );
}
